<template>
  <div class="trk-panel mb-3 conv">
    <div class="trk-panel-head">
      <h2><i class="fa fa-lightbulb-o"></i> Why aren't people converting?</h2>
      <p v-if="view">
        {{ periodLabel }}
        <template v-if="stats.journeys != null"> · {{ stats.journeys }} journeys</template>
        <template v-if="stats.booked != null"> · {{ stats.booked }} booked</template>
        <template v-if="stats.conversionRate != null"> · {{ Number(stats.conversionRate).toFixed(1) }}% converted</template>
        <template v-if="stats.bookedVsPreviousPct != null"> · bookings {{ signed(stats.bookedVsPreviousPct) }} vs previous period</template>
      </p>
    </div>

    <div class="trk-panel-body">
      <p v-if="loading && !view" class="text-muted mb-0">Loading…</p>
      <p v-else-if="error" class="text-danger mb-0">{{ error }}</p>

      <div v-else-if="view" class="conv-grid">
        <section class="conv-col">
          <h3>System calculation</h3>
          <p class="conv-headline">{{ system.headline }}</p>
          <ol v-if="system.reasons && system.reasons.length" class="conv-list">
            <li v-for="(r, i) in system.reasons" :key="i">
              <strong>{{ r.title }}</strong> — {{ r.evidence }}
            </li>
          </ol>
          <p class="conv-meta">Counted from the tracking data · always up to date</p>
        </section>

        <gps-ai-panel
          class="conv-col-ai"
          title="Why people aren't converting"
          purpose="Reads every journey, drop-off, error and failed API call in these dates and names the real cause, with the fix."
          run-label="Run GPS AI analysis"
          :ai="view.ai"
          :running="running"
          @run="run"
        >
          <template v-slot="{ result }">
            <p class="conv-headline">{{ result.headline }}</p>
            <div v-if="result.mainReason" class="conv-reason">
              <strong>{{ result.mainReason.title }}</strong>
              <div>{{ result.mainReason.evidence }}</div>
              <div v-if="result.mainReason.fix" class="conv-fix">Fix: {{ result.mainReason.fix }}</div>
            </div>
            <div v-for="(r, i) in result.otherReasons || []" :key="i" class="conv-reason conv-reason--minor">
              <strong>{{ r.title }}</strong>
              <div>{{ r.evidence }}</div>
              <div v-if="r.fix" class="conv-fix">Fix: {{ r.fix }}</div>
            </div>
            <p v-if="result.dataQuality === 'limited'" class="conv-meta">Little traffic in these dates — treat as early.</p>
          </template>
        </gps-ai-panel>
      </div>
    </div>
  </div>
</template>

<script>
import GpsAiPanel from './GpsAiPanel.vue';

export default {
  name: 'ConversionInsightCard',
  components: { GpsAiPanel },
  props: {
    from: { type: String, default: '' },
    to: { type: String, default: '' }
  },
  data () {
    return { view: null, loading: false, running: false, error: '' };
  },
  computed: {
    stats () {
      return (this.view && this.view.stats) || {};
    },
    system () {
      return (this.view && this.view.system) || {};
    },
    rangeKey () {
      return `${this.from}|${this.to}`;
    },
    periodLabel () {
      if (!this.view) return '';
      return this.view.from === this.view.to ? this.view.from : `${this.view.from} → ${this.view.to}`;
    }
  },
  watch: {
    // One reload when Apply changes both dates together.
    rangeKey: 'load'
  },
  created () {
    this.load();
  },
  methods: {
    params () {
      return { from: this.from, to: this.to };
    },
    load () {
      this.loading = true;
      this.error = '';
      this.$onboardingTrackingService.getGpsAi('conversion', this.params())
        .then((res) => { this.view = (res && res.data) || null; })
        .catch((e) => { this.error = this.message(e, 'Could not load the conversion analysis.'); })
        .finally(() => { this.loading = false; });
    },
    run () {
      this.running = true;
      this.$onboardingTrackingService.runGpsAi('conversion', this.params())
        .then((res) => { this.view = (res && res.data) || this.view; })
        .catch((e) => { this.$awn.alert(this.message(e, 'GPS AI analysis failed.')); })
        .finally(() => { this.running = false; });
    },
    message (e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback;
    },
    signed (n) {
      const v = Number(n);
      return (v > 0 ? '+' : '') + v.toFixed(0) + '%';
    }
  }
};
</script>

<style scoped>
.conv-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 992px) {
  .conv-grid {
    grid-template-columns: 1fr 1fr;
  }
}
.conv-col {
  border: 1px solid #e4e7ea;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
}
.conv-col h3 {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #636b75;
  margin: 0 0 8px;
}
.conv-headline {
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
.conv-list {
  padding-left: 18px;
  margin-bottom: 0;
}
.conv-list li {
  margin-bottom: 4px;
}
.conv-reason {
  font-size: 0.9rem;
  margin-bottom: 8px;
}
.conv-reason--minor {
  color: #3f4a52;
}
.conv-fix {
  color: #2f6f55;
}
.conv-meta {
  font-size: 0.78rem;
  color: #7a828c;
  margin: 10px 0 0;
}
</style>
